import { Link } from 'react-router-dom';
import { ClipboardCheck } from 'lucide-react';

import { useCylinderAuditDue } from '@/hooks/useCylinderAuditDue';

/**
 * CylinderAuditDueBanner — the customer-facing half of the audit-required
 * rule. Shows on every portal page while the account has an open cylinder
 * audit alert, and goes away by itself when the audit is filed (the alert
 * resolves on the audit, not on a click). Deliberately NOT dismissible and
 * deliberately not a blocking modal: the customer can still order — the
 * count happens on the delivery — they just should not be surprised when the
 * driver asks to see every tank.
 */
export function CylinderAuditDueBanner({ compact = false }: { compact?: boolean }) {
  const { data } = useCylinderAuditDue();
  if (!data || data.length === 0) return null;
  const gases = [...new Set(data.map((d) => d.label))].join(', ');
  return (
    <div
      role="status"
      data-testid="cylinder-audit-due"
      className={
        compact
          ? 'flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5 mb-3'
          : 'flex items-start gap-3 bg-amber-50 border-b border-amber-200 px-4 py-2.5 md:px-8'
      }
    >
      <ClipboardCheck size={compact ? 14 : 16} className="mt-0.5 shrink-0 text-amber-700" />
      <p className={compact ? 'text-[11px] leading-relaxed text-amber-900' : 'text-sm text-amber-900'}>
        <span className="font-semibold">Cylinder count due on your next delivery.</span> Our records
        and the tanks on site don’t agree{gases ? ` (${gases})` : ''}, so the driver will count every
        cylinder, take photos and ask for a signature. Please have all tanks accessible — including
        empties. Your{' '}
        <Link to="/cylinders" className="underline">
          Cylinders page
        </Link>{' '}
        shows what we have on record.
      </p>
    </div>
  );
}
